import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod/v4";

export const selfServeOnboardingTimeSeriesDataPointSchema = z.object({
  date: z.string(), // ISO date string
  total: z.number().int().min(0),
  completed: z.number().int().min(0),
  reopened: z.number().int().min(0),
  abandoned: z.number().int().min(0),
  helpRequested: z.number().int().min(0),
});

export const selfServeOnboardingTimeSeriesSchema = z.object({
  dataPoints: z.array(selfServeOnboardingTimeSeriesDataPointSchema),
});

const statusFilterSchema = z.enum([
  "Completed",
  "Reopened",
  "Awaiting approval",
  "In progress",
  "Partial Completion",
  "Help Requested",
]);

const numericFilterSchema = z.object({
  operator: z.enum(["lt", "gt"]),
  value: z.number().min(0),
});

const dateRangeFilterSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

export const getSelfServeOnboardingTimeSeries = unaryRpc({
  name: "getSelfServeOnboardingTimeSeries" as const,
  input: z
    .object({
      timePeriod: z
        .enum([
          "day",
          "week",
          "month",
          "last-month",
          "quarter",
          "all",
          "custom",
        ])
        .default("month"),
      customRange: z.object({ start: z.string(), end: z.string() }).optional(),
      // Advanced filters (matching merchants page)
      statuses: z.array(statusFilterSchema).optional(),
      configurationPushedFilter: z.enum(["yes", "no"]).optional(),
      startDateRange: dateRangeFilterSchema.optional(),
      durationFilter: numericFilterSchema.optional(),
      dealSizeFilter: numericFilterSchema.optional(),
      filterLowIntent: z.boolean().optional(),
      typeFilter: z.enum(["all", "Self-Serve", "Custom"]).optional(),
    })
    .superRefine((data, ctx) => {
      if (data.timePeriod === "custom" && data.customRange == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "customRange is required when timePeriod is 'custom'",
          path: ["customRange"],
        });
      }
    }),
  output: selfServeOnboardingTimeSeriesSchema,
});

export type SelfServeOnboardingTimeSeriesDataPoint = z.infer<
  typeof selfServeOnboardingTimeSeriesDataPointSchema
>;
export type SelfServeOnboardingTimeSeries = z.infer<
  typeof selfServeOnboardingTimeSeriesSchema
>;
export type GetSelfServeOnboardingTimeSeriesInput = z.infer<
  typeof getSelfServeOnboardingTimeSeries.input
>;
export type GetSelfServeOnboardingTimeSeriesOutput = z.infer<
  typeof getSelfServeOnboardingTimeSeries.output
>;
